import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import { ArrowUpRight, Handshake, BadgePercent, CalendarClock, Sparkles } from "lucide-react";

const CAT_ICONS = [Handshake, BadgePercent, CalendarClock, Sparkles];
const CAT_LINKS = {
  "01": "/leistungen#service-01",
  "02": "/finanzierung",
  "03": "/auto-abo",
  "04": "/leistungen#service-08",
};

export default function Services() {
  const { t } = useLang();
  const cats = t.servicesHome?.categories || [];
  return (
    <section id="services" className="py-28 px-6 md:px-10 bg-obsidian" data-testid="services-section">
      <div className="max-w-[1600px] mx-auto">
        <div className="mb-14 max-w-3xl">
          <p className="text-brand-bright text-xs uppercase tracking-[0.3em] mb-4">{t.services.overline}</p>
          <h2 className="font-heading font-black uppercase tracking-tighter text-white text-4xl md:text-6xl leading-[0.95] mb-6">
            {t.services.title}
          </h2>
          {t.services.intro && (
            <p className="text-zinc-400 leading-relaxed text-base md:text-lg">{t.services.intro}</p>
          )}
        </div>

        {/* Kompakte Premium-Kategorien – volle Liste unter /leistungen */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10 border border-white/10">
          {cats.map((c, i) => {
            const Icon = CAT_ICONS[i] || Sparkles;
            return (
              <motion.div
                key={c.n}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.55, delay: (i % 2) * 0.08, ease: [0.22, 1, 0.36, 1] }}
              >
                <Link
                  to={CAT_LINKS[c.n] || "/leistungen"}
                  className="group relative block bg-obsidian p-8 md:p-12 h-full hover:bg-obsidian-surface transition-colors duration-500 overflow-hidden"
                  data-testid={`service-cat-${c.n}`}
                >
                  <div className="flex items-start justify-between mb-10">
                    <Icon className="w-9 h-9 text-brand-bright group-hover:scale-110 transition-transform duration-500" />
                    <span className="font-heading font-black text-5xl md:text-6xl leading-none text-zinc-800 group-hover:text-brand-bright transition-colors duration-500 select-none">
                      {c.n}
                    </span>
                  </div>
                  <h3 className="font-heading font-extrabold uppercase tracking-tight text-white text-2xl md:text-3xl mb-3 group-hover:text-brand-bright transition-colors duration-500">
                    {c.t}
                  </h3>
                  <p className="text-zinc-400 text-sm md:text-base leading-relaxed max-w-md">{c.d}</p>
                  <span className="inline-flex items-center gap-2 mt-8 text-brand-bright text-xs uppercase tracking-[0.2em] font-medium opacity-0 translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500">
                    {t.servicesHome?.more}
                    <ArrowUpRight className="w-4 h-4" />
                  </span>
                  <div className="absolute bottom-0 left-0 h-px w-0 bg-brand-light group-hover:w-full transition-all duration-700" />
                </Link>
              </motion.div>
            );
          })}
        </div>

        <div className="flex justify-center mt-12">
          <Link
            to="/leistungen"
            data-testid="all-services-link"
            className="group inline-flex items-center gap-2 border border-white/25 text-white font-medium uppercase tracking-wider text-sm px-8 py-4 hover:border-brand-light hover:text-brand-bright transition-colors"
          >
            {t.servicesHome?.all}
            <ArrowUpRight className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
          </Link>
        </div>
      </div>
    </section>
  );
}
